import React, { useEffect, useMemo, useRef, useState } from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import EntityDialog from "../../components/EntityDialog";
import User from "../../models/User";
import ErrorBoundary from "../../components/ErrorBoundary";
import { useForm } from "reactcoregk/hooks";
import MultiSelect from "../../components/Inputs/MultiSelect";
import Button from "@material-ui/core/Button";
import { ErrorButtonOutlined } from "../../components/Common/ErrorButton";
import { postData } from "../../helpers/utils";
import { ApiEndpoint } from "../../store/@core/endpoint";
import { EntityType } from "../../store/@core/entityType";
import { useSnackbar } from "notistack";
import { getAllUsers } from "../../store/user/actions";
import { useDispatch } from "react-redux";
import Select from "components/Inputs/Select";
import { USER_EXPIRATION_ENUM, USER_EXPIRATION_OPTIONS } from "constants/enums";
import moment from "moment";

const EditDialog = (props) => {
  const { open, entity, roleOptions, handleClose } = props;
  const [form, handleChange, updateInitial] = useForm(new User());
  const [inProgress, setInProgress] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [expiration, setExpiration] = useState(
    form.expirationDate === null
      ? USER_EXPIRATION_ENUM.NEVER
      : USER_EXPIRATION_ENUM.SPECIFIC_DATE
  );

  useEffect(() => {
    if (form.id) {
      setExpiration(
        form.expirationDate === null
          ? USER_EXPIRATION_ENUM.NEVER
          : USER_EXPIRATION_ENUM.SPECIFIC_DATE
      );
    }
  }, [form]);

  useEffect(() => {
    if (open) {
      const user = new User();
      if (entity.id) {
        user.initFullUser(entity, roleOptions);
        user.roleIds = entity.authorities
          .map((authority) => {
            return roleOptions.find((x) => x.label === authority)?.value;
          })
          .filter((x) => Boolean(x));
      }
      updateInitial(user);
    }
  }, [roleOptions, entity, open, updateInitial]);

  const primaryActionTitle = entity.id
    ? entity.expired === true
      ? "Update and Activate"
      : "Update User"
    : "Add User";

  const shouldActivateRef = useRef(false);

  const onCreateOrUpdate = () => {
    if (entity.id && entity.expired === true) {
      shouldActivateRef.current = true;
    }
    props.createEntity([
      {
        ...form,
        enabled: undefined,
        expirationType: expiration,
        expired: undefined,
        expirationDate:
          expiration === USER_EXPIRATION_ENUM.NEVER
            ? undefined
            : form.expirationDate
            ? moment(form.expirationDate).format("YYYY-MM-DDTHH:mm:ss.SSS")
            : null,
      },
    ]);
  };

  useEffect(() => {
    if (
      shouldActivateRef.current &&
      !props.context.create.isLoading &&
      !props.context.create.error
    ) {
      shouldActivateRef.current = false;
      handleUpdateStatus("enable");
    }
  }, [props.context.create.isLoading]);

  const isValid = Boolean(form.username && form.email && form.roleIds[0]);

  const handleUpdateStatus = async (status) => {
    setInProgress(true);
    try {
      await postData(
        `${ApiEndpoint[EntityType.User]}/${entity.id}/${status}`,
        {}
      );
      const message = status === "enable" ? "activated" : "deactivated";
      enqueueSnackbar(`${entity.username} has been ${message}`, {
        variant: "success",
      });
      dispatch(getAllUsers());
      handleClose();
    } catch (ex) {
      enqueueSnackbar(ex.message, { variant: "error" });
    }
    setInProgress(false);
  };

  const minDateTime = new Date().toISOString().slice(0, 16);

  const handleExpirationDateChange = (e) => {
    const selected = e.target.value;
    const utcNow = new Date().toISOString().slice(0, 16);

    if (selected < utcNow) return; // reject past selection

    handleChange("expirationDate")(e);
  };

  return (
    <EntityDialog
      primaryActionTitle={primaryActionTitle}
      form={form}
      showProgress
      maxWidth={"sm"}
      {...props}
      isValid={isValid}
      createEntity={onCreateOrUpdate}
      updateEntity={onCreateOrUpdate}
    >
      <ErrorBoundary>
        <Grid container direction={"column"}>
          <Grid item>
            <TextField
              variant="standard"
              margin="none"
              placeholder={"Enter username"}
              fullWidth
              value={form.username}
              onChange={handleChange("username")}
              id="name"
              label="Username"
              autoFocus
            />
          </Grid>
          <Grid item>
            <TextField
              variant="standard"
              margin="normal"
              placeholder={"First name"}
              fullWidth
              value={form.firstName}
              onChange={handleChange("firstName")}
              id="firstName"
              label="First name"
            />
          </Grid>
          <Grid item>
            <TextField
              variant="standard"
              margin="normal"
              placeholder={"Last name"}
              fullWidth
              value={form.lastName}
              onChange={handleChange("lastName")}
              id="lastName"
              label="Last name"
            />
          </Grid>
          <Grid item>
            <TextField
              variant="standard"
              margin="normal"
              placeholder={"Enter email ID"}
              fullWidth
              value={form.email}
              onChange={handleChange("email")}
              id="email-id"
              label="Email"
              type={"email"}
            />
          </Grid>
          <Grid item>
            <MultiSelect
              placeholder={"Select roles"}
              fullWidth
              values={form.roleIds}
              onChange={handleChange("roleIds")}
              label="Role"
              controlId={"roleId"}
              options={roleOptions}
              labelId={"roleId"}
            />
          </Grid>
          <Grid item>
            <Select
              placeholder={"Select User Expiration"}
              fullWidth
              value={expiration}
              onChange={(e) => {
                setExpiration(USER_EXPIRATION_ENUM[e.target.value]);
              }}
              label="User Expiration"
              controlId={"userExpiration"}
              options={USER_EXPIRATION_OPTIONS}
              labelId={"userExpiration"}
            />
          </Grid>
          <Grid item>
            <TextField
              variant="standard"
              margin="normal"
              placeholder={"Select User Expiration Date "}
              fullWidth
              type="datetime-local"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: minDateTime,
              }}
              value={form.expirationDate}
              disabled={expiration === USER_EXPIRATION_ENUM.NEVER}
              onChange={handleExpirationDateChange}
              label="User Expiration Date"
            />
          </Grid>
        </Grid>

        {entity.id && entity.expired === false && (
          <Grid item style={{ marginTop: 32 }}>
            {entity.enabled ? (
              <ErrorButtonOutlined
                variant={"outlined"}
                disabled={inProgress}
                onClick={() => handleUpdateStatus("disable")}
              >
                Deactivate
              </ErrorButtonOutlined>
            ) : (
              <Button
                disabled={inProgress}
                onClick={() => handleUpdateStatus("enable")}
                variant={"outlined"}
                color={"primary"}
              >
                Activate
              </Button>
            )}
          </Grid>
        )}
      </ErrorBoundary>
    </EntityDialog>
  );
};

export default EditDialog;
